<template>
    <div class="popular-games-container">
        <div class="popular-games-content">
            <div class="games-section">
                <h1 class="page-title">Popular</h1>
                <p class="page-subtitle">Games from Hacksaw, Inout & Caleta</p>
                <div class="slots-filter-bar">
                    <div class="search-section">
                        <div class="search-input-wrapper">
                            <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <input
                                v-model="searchQuery"
                                type="text"
                                placeholder="Search games"
                                class="search-input"
                            />
                            <button v-if="searchQuery" @click="searchQuery = ''" class="search-clear">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="loading" class="loading-state games-loading">
                    <div class="loading-spinner-simple">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" opacity="0.25"/>
                            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                                <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                            </path>
                        </svg>
                        <p class="loading-text-simple">Loading popular games...</p>
                    </div>
                </div>
                <div v-else-if="error" class="error-state">
                    <p class="error-message">{{ error }}</p>
                    <ButtonGradient @click="loadGames" text="Retry" />
                </div>
                <div v-else-if="filteredGames.length === 0" class="no-games">
                    <p v-if="searchQuery">No games found matching "{{ searchQuery }}"</p>
                    <p v-else>No popular games available (Inout, Hacksaw, Caleta)</p>
                </div>
                <div v-else class="games-grid-wrapper">
                    <div class="games-grid">
                        <SlotsGameCard
                            v-for="game in displayedGames"
                            :key="(game.provider_code || '') + '_' + game.code"
                            :game="game"
                            :provider-code="'SLOTSGATEWAY'"
                            @launch="launchGame"
                        />
                    </div>
                    <div v-if="hasMoreGames" class="show-more-section">
                        <button type="button" class="show-more-btn" @click="showMoreGames">
                            Show more
                            <span class="show-more-count">(+{{ nextShowMoreCount }} games)</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <transition name="game-modal-fade">
            <div v-if="gameSession" class="game-modal-overlay" @click="closeGame" role="dialog" aria-modal="true">
                <div class="game-modal" @click.stop role="document">
                    <div class="game-modal-content">
                        <SlotsGameFrame :session="gameSession" />
                    </div>
                    <button type="button" class="game-modal-close-floating" @click.stop="closeGame" aria-label="Close game">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import ButtonGradient from '@/components/ButtonGradient.vue';
import SlotsGameCard from './SlotsGameCard.vue';
import SlotsGameFrame from './SlotsGameFrame.vue';

const POPULAR_PROVIDERS = ['hacksaw', 'inout', 'caleta'];
const SLOTSGATEWAY_PROVIDER_CODE = 'SLOTSGATEWAY';

function normalizeGame(g) {
    const id = g.id_hash || g.id || g.game_id || g.code;
    const name = g.name || g.game_name || g.title || '';
    const image = g.image || g.banner || g.thumb || g.small_image || g.icon || '';
    const provider = g.provider_code || g.provider_name || g.provider || g.category || SLOTSGATEWAY_PROVIDER_CODE;
    return {
        code: id,
        name,
        provider_code: provider,
        provider_name: g.provider_name || g.provider || provider,
        image,
        status: g.status != null ? g.status : 1,
    };
}

function getProviderKey(g) {
    return (g.provider_code || g.provider_name || g.provider || g.category || '').toString().trim().toLowerCase();
}

export default {
    name: 'PopularGamesMain',
    components: {
        ButtonGradient,
        SlotsGameCard,
        SlotsGameFrame
    },
    data() {
        return {
            loading: false,
            error: null,
            popularGames: [],
            searchQuery: '',
            gamesDisplayLimit: 21
        };
    },
    computed: {
        ...mapGetters('slots', ['gameSession']),
        filteredGames() {
            if (!this.searchQuery.trim()) return this.popularGames;
            const q = this.searchQuery.toLowerCase().trim();
            return this.popularGames.filter(game =>
                (game.name && game.name.toLowerCase().includes(q)) ||
                (game.code && game.code.toLowerCase().includes(q))
            );
        },
        displayedGames() {
            return this.filteredGames.slice(0, this.gamesDisplayLimit);
        },
        hasMoreGames() {
            return this.filteredGames.length > this.gamesDisplayLimit;
        },
        nextShowMoreCount() {
            const remaining = this.filteredGames.length - this.gamesDisplayLimit;
            return Math.min(31, remaining);
        }
    },
    async mounted() {
        await this.loadGames();
        document.addEventListener('keydown', this.handleGameModalKeydown);
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.handleGameModalKeydown);
        document.body.classList.remove('slot-game-playing');
    },
    methods: {
        ...mapActions('slots', ['resetGameSession']),
        async loadGames() {
            this.loading = true;
            this.error = null;
            try {
                const [slotsRes, liveRes] = await Promise.all([
                    axios.get('/slotsgateway/games', { params: { currency: 'USD', category: 'slots' } }),
                    axios.get('/slotsgateway/games', { params: { currency: 'USD', category: 'live_casino' } })
                ]);
                const slots = (slotsRes.data.success && slotsRes.data.games) ? slotsRes.data.games.map(normalizeGame) : [];
                const live = (liveRes.data.success && liveRes.data.games) ? liveRes.data.games.map(normalizeGame) : [];
                const merged = [...slots, ...live];
                this.popularGames = merged.filter(g => POPULAR_PROVIDERS.includes(getProviderKey(g)));
            } catch (e) {
                this.error = e.response?.data?.message || e.message || 'Failed to load popular games';
                this.popularGames = [];
            } finally {
                this.loading = false;
            }
        },
        showMoreGames() {
            this.gamesDisplayLimit += 31;
        },
        closeGame() {
            this.resetGameSession();
        },
        launchGame() {
            const sidebar = document.querySelector('aside#sidebar-left');
            if (sidebar && !sidebar.classList.contains('collapsed') && window.innerWidth > 1024) {
                const btn = sidebar.querySelector('.menu-toggle');
                if (btn) btn.click();
            }
        },
        handleGameModalKeydown(e) {
            if (e.key === 'Escape' && this.gameSession) this.closeGame();
        }
    },
    watch: {
        gameSession(v) {
            document.body.classList.toggle('slot-game-playing', !!v);
        },
        searchQuery() {
            this.gamesDisplayLimit = 21;
        }
    }
};
</script>

<style scoped>
.popular-games-container {
    width: 100%;
    min-height: calc(100vh - 80px);
    padding: var(--spacing-lg);
}

.popular-games-content {
    max-width: 1400px;
    margin: 0 auto;
}

.page-title {
    font-size: 32px;
    font-weight: 800;
    margin: 0 0 4px 0;
    background: var(--accent-main-txt);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: 1px;
}

.page-subtitle {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.65);
    margin: 0 0 var(--spacing-xl) 0;
}

.games-section {
    margin-bottom: var(--spacing-2xl);
    border-radius: 20px;
    padding: 24px;
}

.slots-filter-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
}

.slots-filter-bar .search-section {
    flex: 1;
    min-width: 200px;
}

.search-input-wrapper {
    position: relative;
    max-width: 400px;
}

.search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7c8a;
    pointer-events: none;
}

.search-input {
    width: 100%;
    height: 48px;
    padding: 0 48px 0 48px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    background: rgba(255, 255, 255, 0.05);
    border: 2px solid rgba(255, 255, 255, 0.15);
    outline: none;
}

.search-input:focus {
    border-color: var(--color-copper);
    background: rgba(255, 255, 255, 0.08);
}

.search-input::placeholder {
    color: #6b7c8a;
}

.search-clear {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    color: #fff;
    cursor: pointer;
}

.loading-state, .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 280px;
    gap: var(--spacing-lg);
}

.loading-spinner-simple {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.loading-spinner-simple svg {
    color: var(--accent-yellow);
}

.loading-text-simple {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
}

.error-message {
    color: #ff6b6b;
    font-size: 16px;
    text-align: center;
}

.no-games {
    text-align: center;
    padding: var(--spacing-2xl);
    color: rgba(255, 255, 255, 0.6);
}

.games-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 20px;
    width: 100%;
}

.show-more-section {
    display: flex;
    justify-content: center;
    padding: var(--spacing-md) 0;
}

.show-more-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 12px 24px;
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    background: var(--accent-btn-purple, #4a2c7a);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    cursor: pointer;
}

.show-more-btn:hover {
    background: var(--accent-btn-light-purple, #5a3c8a);
}

.game-modal-fade-enter-active,
.game-modal-fade-leave-active { transition: opacity 0.25s ease; }
.game-modal-fade-enter-from,
.game-modal-fade-leave-to { opacity: 0; }

/* Slot game modal: full screen */
.game-modal-overlay {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    background: #000;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    z-index: 9999999 !important;
    padding: 0 !important;
    margin: 0 !important;
    box-sizing: border-box !important;
}

.game-modal {
    position: relative;
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    background: #000;
    border: none;
    border-radius: 0;
    overflow: hidden;
    z-index: 10000000;
}

.game-modal-content {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    overflow: hidden;
}

.game-modal-close-floating {
    position: absolute !important;
    top: 12px !important;
    right: 12px !important;
    width: 48px !important;
    height: 48px !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
    background: rgba(0, 0, 0, 0.6) !important;
    border: 2px solid rgba(255, 255, 255, 0.25) !important;
    border-radius: 50% !important;
    color: #fff !important;
    cursor: pointer !important;
    z-index: 10000003 !important;
}

.game-modal-close-floating:hover {
    background: rgba(0, 0, 0, 0.85) !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
}
</style>
